import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useRef, useState, type FormEvent, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';

import { AuthLayout } from '@/layouts/AuthLayout';
import {
  clearSession,
  getPartialToken,
  getSessionEmail,
  isTokenExpired,
  startSession,
} from '@/lib/session';

import styles from './LoginPage.module.scss';
import setupStyles from './MfaSetupPage.module.scss';
import {
  platformConfirmMfa,
  platformSetupMfa,
  type MfaSetupResponse,
} from '../api/platform-auth.api';

type Step =
  | { kind: 'loading' }
  | { kind: 'failed'; message: string }
  | { kind: 'scan'; setup: MfaSetupResponse }
  | { kind: 'codes'; recoveryCodes: string[]; accessToken: string };

/** Agrupa el secreto en bloques de 4 para dictarlo o tipearlo sin errores. */
function groupSecret(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim();
}

/**
 * OS-1708 — enrolamiento MFA obligatorio en el primer login:
 * escanear el QR → confirmar con un código → guardar los códigos de recuperación → entrar.
 * El QR se genera en el navegador: el secreto nunca sale hacia un servicio externo.
 */
export function MfaSetupPage(): JSX.Element {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>({ kind: 'loading' });
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [savedCodes, setSavedCodes] = useState(false);
  const [copied, setCopied] = useState('');
  // StrictMode monta dos veces en desarrollo: un segundo setup generaría otro secreto y el QR
  // mostrado podría no coincidir con el pendiente en el servidor.
  const requested = useRef(false);

  useEffect(() => {
    if (requested.current) {
      return;
    }
    requested.current = true;

    const token = getPartialToken();
    if (!token) {
      void navigate('/login', { replace: true });
      return;
    }
    if (isTokenExpired(token)) {
      clearSession();
      void navigate('/login?expired=1', { replace: true });
      return;
    }

    platformSetupMfa(token)
      .then((setup) => setStep({ kind: 'scan', setup }))
      .catch((err: unknown) => setStep({ kind: 'failed', message: (err as Error).message }));
  }, [navigate]);

  async function handleConfirm(e: FormEvent): Promise<void> {
    e.preventDefault();
    const token = getPartialToken();
    if (!token || isTokenExpired(token)) {
      clearSession();
      void navigate('/login?expired=1');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const result = await platformConfirmMfa(token, code);
      if (!result.accessToken) {
        throw new Error('No se pudo abrir la sesión. Vuelve a iniciar sesión.');
      }
      setStep({
        kind: 'codes',
        recoveryCodes: result.recoveryCodes,
        accessToken: result.accessToken,
      });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  async function copy(text: string, label: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
    } catch {
      setCopied('');
    }
  }

  function downloadCodes(codes: string[]): void {
    const content = [
      'OpsSynk Admin — códigos de recuperación MFA',
      `Cuenta: ${getSessionEmail()}`,
      'Cada código sirve una sola vez. Guárdalos en un lugar seguro.',
      '',
      ...codes,
      '',
    ].join('\n');
    const url = URL.createObjectURL(new Blob([content], { type: 'text/plain' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'opssynk-admin-codigos-recuperacion.txt';
    link.click();
    URL.revokeObjectURL(url);
  }

  function enterConsole(accessToken: string): void {
    startSession(accessToken, getSessionEmail());
    void navigate('/dashboard', { replace: true });
  }

  if (step.kind === 'loading') {
    return (
      <AuthLayout title="Configurar MFA" subtitle="Preparando tu autenticador…">
        <p className={styles.hint} role="status">
          Generando el código QR…
        </p>
      </AuthLayout>
    );
  }

  if (step.kind === 'failed') {
    return (
      <AuthLayout title="Configurar MFA" subtitle="No pudimos iniciar la configuración">
        <div className={styles.form}>
          <p className={styles.error} role="alert">
            {step.message}
          </p>
          <button
            type="button"
            className={styles.submitBtn}
            onClick={() => {
              clearSession();
              void navigate('/login');
            }}
          >
            Volver al inicio de sesión
          </button>
        </div>
      </AuthLayout>
    );
  }

  if (step.kind === 'codes') {
    const allCodes = step.recoveryCodes.join('\n');
    return (
      <AuthLayout
        title="Códigos de recuperación"
        subtitle="Guárdalos ahora: no se volverán a mostrar"
      >
        <div className={styles.form}>
          <p className={styles.hint}>
            Si pierdes tu autenticador, cada código te permite entrar una sola vez.
          </p>
          <ol className={setupStyles.codes} aria-label="Códigos de recuperación">
            {step.recoveryCodes.map((recoveryCode) => (
              <li key={recoveryCode}>
                <code>{recoveryCode}</code>
              </li>
            ))}
          </ol>
          <div className={setupStyles.actions}>
            <button
              type="button"
              className={styles.linkBtn}
              onClick={() => void copy(allCodes, 'codes')}
            >
              {copied === 'codes' ? 'Copiados' : 'Copiar códigos'}
            </button>
            <button
              type="button"
              className={styles.linkBtn}
              onClick={() => downloadCodes(step.recoveryCodes)}
            >
              Descargar .txt
            </button>
          </div>
          <label className={setupStyles.confirm}>
            <input
              type="checkbox"
              checked={savedCodes}
              onChange={(e) => setSavedCodes(e.target.checked)}
            />
            Guardé mis códigos de recuperación en un lugar seguro
          </label>
          <button
            type="button"
            className={styles.submitBtn}
            disabled={!savedCodes}
            onClick={() => enterConsole(step.accessToken)}
          >
            Entrar a la consola
          </button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Configurar MFA" subtitle="Obligatorio para acceder a la consola">
      <form className={styles.form} onSubmit={(e) => void handleConfirm(e)}>
        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}

        <p className={styles.hint}>
          1. Escanea el código con tu app autenticadora (Google Authenticator, Microsoft
          Authenticator, 1Password…).
        </p>
        <div className={setupStyles.qr}>
          <QRCodeSVG
            value={step.setup.otpauthUri}
            size={184}
            includeMargin
            role="img"
            aria-label="Código QR para configurar el autenticador"
          />
        </div>
        <p className={styles.hint}>¿No puedes escanearlo? Ingresa este código manualmente:</p>
        <div className={setupStyles.secret}>
          <code>{groupSecret(step.setup.secret)}</code>
          <button
            type="button"
            className={styles.linkBtn}
            onClick={() => void copy(step.setup.secret, 'secret')}
          >
            {copied === 'secret' ? 'Copiado' : 'Copiar'}
          </button>
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="code">
            2. Ingresa el código de 6 dígitos que muestra la app
          </label>
          <input
            id="code"
            type="text"
            inputMode="numeric"
            pattern="\d{6}"
            maxLength={6}
            className={styles.input}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="123456"
            required
            autoComplete="one-time-code"
          />
        </div>

        <button
          type="submit"
          className={styles.submitBtn}
          disabled={submitting || code.length !== 6}
        >
          {submitting ? 'Verificando…' : 'Activar MFA'}
        </button>
      </form>
    </AuthLayout>
  );
}
